/**
 * The run's hair pass — ADR-039 T2a: **a person's confirmed ATTACHED hair
 * softens, every run.** Part of the soft-tissue pass, and like it LAST of the
 * weight passes (run-document runs it right after the J2 leaves are seated), so
 * no earlier pass reads or undoes it and the paint it inherits is final.
 *
 * WHAT RUNS, only when `target.hair` is confirmed, its zone was cut on THIS
 * mesh (fingerprint), and the treatment is `attached`:
 *
 *  1. the skull is READ BACK from the declaration on the run's subject through
 *     the Hair card's own door (`hairSubject` + `readHair` in the tail
 *     detector's frame — never re-measured), for the brow and the Head;
 *  2. `softenAttachedHair` (kernel) answers per vertex: Harry's own weights
 *     feathered into the border, the leaf's share, the held set;
 *  3. the weights are WRITTEN (`writeSubjectSkin`, the inverse of
 *     `subjectArrays`) — the confirmed region's vertices only;
 *  4. the leaf `HairJiggle_graft` (+ tip) is grafted on the rig through J2's
 *     `graftSoftTissueLeaves` (a `Hair` region, hung from the Head, pivoting at
 *     the skull's centre), and its share is returned as a PENDING seat for the
 *     ONE seat (`bindGraftedDigits`, donor = the Head: the leaf takes only a
 *     share of the Head's weight, so at rest the hair follows the head exactly
 *     as before);
 *  5. the surface modes' patch is returned (the region and its held set) for
 *     J4's graft, which runs after the seat with every other leaf's.
 *
 * WHAT DOES NOT RUN, said in one sentence on the record (fail loud, never
 * silent): no hair confirmed (no record at all — an unconfirmed offer changes
 * nothing); a confirmed `free` / `mixed` hair (spring chains are T3 — nothing
 * changes); a zone cut on another mesh; a skull that no longer reads back; a
 * region with nothing to soften; a rig that ALREADY wears `HairJiggle` (a
 * reopened export: its leaf, spring, weights and surface targets are the
 * file's, adopted as written — never a second leaf in the same hair).
 *
 * Depends on: kernel (hair-soften, leaf-bones, fingerprints), adapter-three
 * (subject arrays, the skin write), viewport/hair-subject, tail detection.
 * Depended on by: session/run-document, the hair card (the record), the door test.
 */
import type { Object3D, SkinnedMesh } from 'three';
import {
  detectTail,
  fingerprintMesh,
  fingerprintMismatch,
  graftSoftTissueLeaves,
  HairSoftenRefusal,
  HAIR_PEAK_SHARE,
  softenAttachedHair,
  SoftTissueRefusal,
  SOFT_TISSUE_SLOT,
  type AnimatorDocument,
  type EvidenceMarker,
  type HairSoftenResult,
  type HairTreatment,
  type MeshFingerprint,
  type RigDescriptor,
  type VertexInfluence,
} from '@harryhausen/kernel';
import { subjectArrays, writeSubjectSkin } from '@harryhausen/adapter-three';
import { hairSubject, readHair } from '../viewport/hair-subject.js';
import type { SoftTissueGraftRecord } from '../jiggle/soft-tissue-run.js';

export type HairSoftenStatus = 'softened' | 'adopted' | 'deferred' | 'refused';

/** What the hair pass did (or why not), in numbers and one sentence for the card. */
export interface HairSoftenRecord {
  readonly status: HairSoftenStatus;
  readonly treatment: HairTreatment;
  /** The card's one sentence. */
  readonly sentence: string;
  /** The status line's reading (numbers), or null. */
  readonly reading: string | null;
  /** The kernel's answer when it softened. */
  readonly numbers: {
    readonly welded: number;
    readonly reweighted: number;
    readonly weighted: number;
    readonly weightedWelded: number;
    readonly heldBy: HairSoftenResult['heldBy'];
    readonly face: number;
    readonly evicted: number;
    readonly foreignAtBorder: number;
    readonly foreignKept: number;
    readonly stripped: HairSoftenResult['harry']['stripped'];
    readonly seam: HairSoftenResult['seam'];
    readonly skullR: number;
    readonly leafLength: number;
  } | null;
}

/**
 * The record's sentence, finished once J4 has run (the run grafts the surface
 * after the seat): how many modes ride the hair's bounce, or why none do — a
 * refused patch keeps its bounce and says so (ADR-038 §14.6).
 */
export function finishHairRecord(
  record: HairSoftenRecord | null,
  surface: { readonly modes: readonly unknown[]; readonly refusal: string | null } | null,
  surfaceOn: boolean,
): HairSoftenRecord | null {
  if (record === null || record.status !== 'softened') return record;
  const clause = !surfaceOn
    ? 'the surface ripple is off'
    : surface === null
      ? 'no surface ripple'
      : surface.refusal !== null
        ? `no surface ripple (${surface.refusal.replace(/^surface modes refused: /, '')})`
        : `${surface.modes.length} surface modes ride its bounce`;
  return { ...record, sentence: `${record.sentence}; ${clause}.` };
}

export interface HairSoftenPlan {
  readonly rig: RigDescriptor;
  readonly record: HairSoftenRecord | null;
  /** The leaf's graft record (region `Hair`) — it joins the run's soft-tissue grafts, so its spring plays and exports. */
  readonly graft: SoftTissueGraftRecord | null;
  /** The ONE seat's call: the leaf + tip and its shares of the Head's weight. */
  readonly pending: { readonly boneNames: readonly string[]; readonly influences: readonly VertexInfluence[]; readonly donor: string } | null;
  /** J4's patch: the confirmed region and the vertices held (φ = 0). */
  readonly patch: { readonly vertices: readonly number[]; readonly faces: readonly number[]; readonly held: ReadonlySet<number> } | null;
  readonly markers: readonly EvidenceMarker[];
}

/**
 * THE PATCH OF A FILE THAT ALREADY WEARS ITS HAIR (Codex P2, #233): a reopened export adopts its
 * `surface.Hair.*` targets by name, so the record that drives them must be solved on the patch they
 * were solved on — not the leaf's own flesh. The targets say which vertices moved (φ ≠ 0, the FREE
 * set); the free set's one-ring is held (φ = 0). That is exactly the Dirichlet problem the export
 * solved: every free vertex keeps every neighbour it had, the held ones at zero, so λ, φ and the
 * participation come back as written. Null when the mesh carries no hair targets (then the leaf's
 * own flesh is the patch, as for any adopted mass).
 */
export function adoptedHairPatch(mesh: SkinnedMesh): { vertices: number[]; faces: number[]; held: Set<number> } | null {
  const dict = mesh.morphTargetDictionary ?? {};
  const deltas = mesh.geometry.morphAttributes['position'] ?? [];
  const free = new Set<number>();
  for (const [name, slot] of Object.entries(dict)) {
    if (!name.startsWith('surface.Hair.')) continue;
    const a = deltas[slot];
    if (a === undefined) continue;
    for (let v = 0; v < a.count; v++) if (a.getX(v) !== 0 || a.getY(v) !== 0 || a.getZ(v) !== 0) free.add(v);
  }
  if (free.size === 0) return null;
  const index = mesh.geometry.getIndex();
  if (index === null) return null;
  const faces: number[] = [];
  const vertices = new Set<number>();
  for (let f = 0; f < index.count / 3; f++) {
    const t = [index.getX(f * 3), index.getX(f * 3 + 1), index.getX(f * 3 + 2)];
    if (!t.some((v) => free.has(v))) continue;
    faces.push(f);
    t.forEach((v) => vertices.add(v));
  }
  const held = new Set([...vertices].filter((v) => !free.has(v)));
  return { vertices: [...vertices], faces, held };
}

const NONE = (rig: RigDescriptor, record: HairSoftenRecord | null, markers: EvidenceMarker[] = []): HairSoftenPlan => ({ rig, record, graft: null, pending: null, patch: null, markers });
const fmt = (n: number): string => n.toLocaleString('en-US');

/**
 * Plan (and write the weights of) the hair's softening. `zoneFingerprint` is the
 * run's (a centred tail turned the rest flesh: zones stay valid against the mesh
 * before the turn); null = the live mesh.
 */
export function planHairSoftening(doc: AnimatorDocument, scene: Object3D, rig: RigDescriptor, zoneFingerprint: MeshFingerprint | null): HairSoftenPlan {
  const hair = doc.target.hair;
  if (hair === undefined) return NONE(rig, null);
  const treatment = hair.treatment;
  const say = (status: HairSoftenStatus, sentence: string, reading: string | null = null): HairSoftenPlan =>
    NONE(rig, { status, treatment, sentence, reading, numbers: null }, [{ code: `hair-${status}`, detail: { treatment, why: sentence } }]);
  if (treatment !== 'attached') {
    return say('deferred', `${treatment === 'free' ? 'Free' : 'Mixed'} hair will move on spring chains, which are not built yet — nothing changes on this hair for now.`);
  }
  const zone = (doc.zones ?? []).find((z) => z.id === hair.zoneId);
  if (zone === undefined) return say('refused', `The hair's zone "${hair.zoneId}" is missing — withdraw the hair and confirm it again.`);
  const arrays = subjectArrays(scene);
  const mismatch = fingerprintMismatch(zone.meshFingerprint, zoneFingerprint ?? fingerprintMesh(arrays.positions, arrays.indices));
  if (mismatch !== null) return say('refused', `Not softened: the hair was confirmed ${mismatch} — withdraw it and confirm again.`);
  const slot = SOFT_TISSUE_SLOT.Hair;
  const worn = rig.bones.find((b) => b.name === slot || b.name === `${slot}_graft`);
  if (worn !== undefined || rig.mapping.some((m) => m.ontologyId === slot)) {
    return say('adopted', `This file already wears its hair leaf (${worn?.name ?? slot}); its weights, spring and surface are kept as written.`);
  }
  if (arrays.skin === null) return say('refused', 'Not softened: the body is not skinned.');

  // The skull, read back on THIS subject through the card's door (never re-measured).
  const subject = hairSubject(scene);
  const frame = detectTail(subject.arrays).frame;
  if (frame === null) return say('refused', 'Not softened: no body frame to read the skull in (the legs do not reach the floor).');
  const reading = readHair(subject, frame, hair.skull);
  if ('refused' in reading) return say('refused', `Not softened: ${reading.refused}.`);
  const sk = reading.skull;
  // The face's heights (T1's face mask: the brow at eye height + 0.35 F, the chin), bind-world: the head frame's v is height above the Head joint.
  const headJoint = subject.arrays.bones.find((b) => b.name === reading.headBone)?.worldPosition;
  if (headJoint === undefined) return say('refused', `Not softened: no "${reading.headBone}" bone on this body.`);
  const face = { brow: headJoint.y + sk.vE + 0.35 * sk.F, chin: headJoint.y + sk.vChin };

  let soft: HairSoftenResult;
  try {
    soft = softenAttachedHair({
      positions: arrays.positions,
      indices: arrays.indices,
      skin: arrays.skin,
      bones: arrays.bones,
      headBone: reading.headBone,
      regionFaces: zone.faces,
      skull: hair.skull,
      face,
    });
  } catch (err) {
    if (err instanceof HairSoftenRefusal) return say('refused', `Not softened: ${err.message}.`);
    throw err;
  }

  // The leaf: J2's graft with a `Hair` region, hung from the Head, root at the skull's centre.
  let grafted;
  try {
    grafted = graftSoftTissueLeaves(rig, [{ id: 'Hair', root: soft.root, apex: soft.tip }], new Map([['Hair', reading.headBone]]));
  } catch (err) {
    if (err instanceof SoftTissueRefusal) return say('refused', `Not softened: ${err.message}.`);
    throw err;
  }
  const leaf = grafted.leaves[0]!;
  // Only now, with every refusal behind us, does the paint change: the region's vertices, nothing else.
  writeSubjectSkin(scene, soft.vertices, soft.index, soft.weight);
  const influences: VertexInfluence[] = [];
  soft.vertices.forEach((v, q) => {
    if (soft.share[q]! > 0) influences.push({ vertexIndex: v, boneId: leaf.bone, weight: soft.share[q]! });
  });
  const held = new Set<number>();
  soft.vertices.forEach((v, q) => {
    if (soft.held[q] === 1) held.add(v);
  });
  const top = soft.harry.stripped[0];
  // Counts a person reads are WELDED vertices (a UV seam's twins are one point of hair), as T1's are.
  // The surface's clause is added once J4 has run (`finishHairRecord`).
  const sentence = `Softened: ${fmt(soft.welded)} vertices now on Harry's head weights, ${fmt(soft.heldBy.total)} held at the scalp, the body and the face`;
  const readingLine =
    `Hair T2: ${fmt(soft.welded)} welded vertices, ${fmt(soft.reweighted)} raw reweighted (foreign weight kept at the border ${(100 * soft.foreignKept).toFixed(0)}%) · held ${fmt(soft.heldBy.ring)} border, ${fmt(soft.heldBy.scalp)} scalp, ${fmt(soft.heldBy.contact)} body contact, ${fmt(soft.heldBy.face)} face · ` +
    `${top === undefined ? 'nothing stripped' : `stripped ${top.bone} from ${fmt(top.vertices)}${soft.harry.stripped.length > 1 ? ` (+${soft.harry.stripped.length - 1} bones)` : ''}`} · ` +
    `seam ${soft.seam.hard.crossing.toFixed(2)} → ${soft.seam.feathered.crossing.toFixed(2)} across the border · leaf ${(leaf.length * 100).toFixed(1)} cm, peak share ${HAIR_PEAK_SHARE}.`;
  const record: HairSoftenRecord = {
    status: 'softened',
    treatment,
    sentence,
    reading: readingLine,
    numbers: {
      welded: soft.welded,
      reweighted: soft.reweighted,
      weighted: soft.weighted,
      weightedWelded: soft.weightedWelded,
      heldBy: soft.heldBy,
      face: soft.face,
      evicted: soft.evicted,
      foreignAtBorder: soft.foreignAtBorder,
      foreignKept: soft.foreignKept,
      stripped: soft.harry.stripped,
      seam: soft.seam,
      skullR: soft.skullR,
      leafLength: leaf.length,
    },
  };
  const graft: SoftTissueGraftRecord = {
    region: 'Hair',
    source: 'declared',
    zoneId: zone.id,
    tier: 'strong',
    prominence: leaf.length / 1.5,
    bone: leaf.bone,
    tip: leaf.tip,
    parent: leaf.parent,
    head: leaf.head,
    tail: leaf.tail,
    length: leaf.length,
    radius: soft.skullR,
    vertices: soft.vertices.length,
    verticesWeighted: influences.length,
    peakShare: HAIR_PEAK_SHARE,
    clothFacesSkipped: 0,
    reading: readingLine,
  };
  return {
    rig: grafted.rig,
    record,
    graft,
    pending: { boneNames: [leaf.bone, leaf.tip], influences, donor: reading.headBone },
    patch: { vertices: soft.vertices, faces: zone.faces, held },
    markers: [
      {
        code: 'hair-softened',
        detail: {
          welded: soft.welded,
          reweighted: soft.reweighted,
          weighted: influences.length,
          heldRing: soft.heldBy.ring,
          heldScalp: soft.heldBy.scalp,
          heldContact: soft.heldBy.contact,
          heldFace: soft.heldBy.face,
          face: soft.face,
          evicted: soft.evicted,
          seamHard: Number(soft.seam.hard.crossing.toFixed(4)),
          seamFeathered: Number(soft.seam.feathered.crossing.toFixed(4)),
          leafCm: Number((leaf.length * 100).toFixed(2)),
        },
      },
    ],
  };
}
